import { useMemo, useRef } from "react";
import { type ColumnFiltersState } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { useAppSelector } from "../../redux/hooks";
import { useSimpleCan } from "../../config/casl/casl";
import { useBlockWindowQuery } from "../../components/table/useBlockWindowQuery";
import { useGetV4AdvReportByAd } from "../perf_reports/api/ads/v4AdvReportByAd";
import { type TargetQueryV4Row, useGetV4TargetReportByTarget } from "../perf_reports/api/target/v4TargetReportByTarget";
import { useTargetReportColumns } from "../perf_reports/target_report/useTargetReportColumns";
import useTargetBulkActions from "../../hooks/useTargetBulkActions";
import AddTargetsWidget from "../../components/custom/category/advertisements/AddTargetsWidget";
import { usePersistedTableState } from "../../components/table/usePersistedTableState";
import { useServerTable } from "../../components/table/tableFeatures";
import { Table } from "../../components/table/Table";
import { RT8Pagination } from "../../components/table/Pagination";
import { RT8ColumnSelector } from "../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../components/fields/base/TextField";
import { RT8FilterWidget } from "../merchandising/components/tables/FilterWidget";
import { BulkActionButton } from "../../components/table/BulkActionButton";
import { TableErrorWrapper } from "../../components/table/TableErrorWrapper";
import { resetTable, idsToVisibilityState, selectionStateToSelectedIds } from "../../components/table/utils";
import { Button } from "../../components/core/basic/Button";
import { DIMENSION_TYPES, DEFAULT_TARGET_REPORT_SELECTED_COLUMNS } from "../str/utils/reportingConstants";
import {
  KEYWORD_MATCH_TYPE_OPTIONS,
  TARGET_STATE_OPTIONS,
  AMAZON_TARGET_SOURCE_OPTIONS,
  WALMART_TARGET_SOURCE_OPTIONS,
  BID_TREND_OPTIONS,
} from "../advertising/filters/targetLifecycleFilters";
import { HStack, VStack, Wrap } from "../../../styled-system/jsx";
import { css } from "../../../styled-system/css";

/** The search query an expander opened on, with the advertised keywords and targets matching it. */
export interface SQPRTargetRow {
  search_query: string;
  adv_targets?: (number | null)[];
  adv_keywords?: (number | null)[];
}

export type SQPRTargetType = "keyword" | "target";

type TargetTableRow = TargetQueryV4Row & { ad_name?: string; managed: boolean };

const targetIdOf = (targetType: SQPRTargetType, targetRow: TargetQueryV4Row) =>
  targetType === "target" ? targetRow.adv_target_id : targetRow.adv_keyword_id;

type AdsMapping = Record<string, { ad_id: number; ad_name: string; managed: string }>;

export const SQPRTargetTable = ({ targetType, row }: { targetType: SQPRTargetType; row: SQPRTargetRow }) => {
  const { t } = useTranslation(["sqpr"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  const byAdGetter = useGetV4AdvReportByAd();
  const columns = useTargetReportColumns(targetType);

  const defaultHiddenCols = useMemo(
    () =>
      idsToVisibilityState(
        DEFAULT_TARGET_REPORT_SELECTED_COLUMNS,
        columns.flatMap((col) => (col.id ? [col.id] : [])),
      ),
    [columns],
  );

  const { state, initialState, setters } = usePersistedTableState({
    persistKey: `sqpr-expander-${targetType}`,
    initialState: {
      columnVisibility: defaultHiddenCols,
      columnOrder: DEFAULT_TARGET_REPORT_SELECTED_COLUMNS,
      columnPinning: { start: ["text"], end: [] },
      sorting: [{ id: "impressions", desc: true }],
    },
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const dimensions = targetType === "target" ? DIMENSION_TYPES.TARGET_ID : DIMENSION_TYPES.KEYWORD_ID;
  const ids = useMemo(
    () => (targetType === "target" ? row.adv_targets : row.adv_keywords)?.filter((id) => id != null) ?? [],
    [targetType, row.adv_targets, row.adv_keywords],
  );

  const byTargetGetter = useGetV4TargetReportByTarget(targetType, dimensions);

  const idFilters = useMemo<ColumnFiltersState>(() => {
    return ids.length > 0 ? [{ id: dimensions, value: { op: "anyof", value: ids } }] : [];
  }, [ids, dimensions]);

  const combinedFilters = useMemo(() => {
    return [...idFilters, ...state.columnFilters];
  }, [idFilters, state.columnFilters]);

  const targetQuery = useBlockWindowQuery(
    byTargetGetter,
    {
      pagination: state.pagination,
      sorting: state.sorting,
      filters: combinedFilters,
      search: debounceSearch,
    },
    { enabled: ids.length > 0 },
  );

  const adIdsOnPage = useMemo(() => {
    return Array.from(new Set((targetQuery.data?.data ?? []).map((row) => row.ad_id)));
  }, [targetQuery.data?.data]);

  const advertisementQuery = useQuery({
    ...byAdGetter({
      pagination: { pageIndex: 0, pageSize: adIdsOnPage.length },
      sorting: [],
      filters: adIdsOnPage.length > 0 ? [{ id: "ad_id", value: { op: "anyof", value: adIdsOnPage } }] : [],
      search: "",
    }),
    enabled: adIdsOnPage.length > 0,
  });

  const isAdDataLoading =
    adIdsOnPage.length > 0 &&
    advertisementQuery.isFetching &&
    (!advertisementQuery.data || advertisementQuery.isPlaceholderData);

  const adsMapping = useMemo(() => {
    const ads = advertisementQuery.data?.data ?? [];
    const mapping: AdsMapping = {};
    for (const ad of ads) {
      mapping[ad.ad_id.toString()] = { ad_id: ad.ad_id, ad_name: ad.ad_name, managed: ad.managed };
    }
    return mapping;
  }, [advertisementQuery.data?.data]);

  const prefilledTargets = useMemo(() => {
    const targets =
      targetType === "target" ?
        [{ text: row.search_query, matchTypes: [] }]
      : [{ text: row.search_query, matchTypes: ["exact", "phrase", "broad"] }];
    return { targets };
  }, [targetType, row.search_query]);

  const tableData = useMemo<TargetTableRow[] | undefined>(() => {
    return targetQuery.data?.data.map((targetRow) => {
      const ad = adsMapping[targetRow.ad_id.toString()];
      return {
        ...targetRow,
        ad_name: ad?.ad_name,
        managed: ad?.managed.toLowerCase() === "yes",
      };
    });
  }, [targetQuery.data?.data, adsMapping]);

  const unmanagedTargetIds = useMemo(() => {
    return (tableData ?? [])
      .filter((targetRow) => !targetRow.managed)
      .map((targetRow) => targetIdOf(targetType, targetRow))
      .filter((id) => id != null);
  }, [tableData, targetType]);

  // Row data for every target seen on any page, so a selection that spans pages still reaches the bulk actions with
  // its tags, source and scheduled actions. Only ids still in `rowSelection` are read from it.
  const seenRows = useRef(new Map<string, TargetTableRow>());
  for (const targetRow of tableData ?? []) {
    const id = targetIdOf(targetType, targetRow);
    if (id != null) seenRows.current.set(String(id), targetRow);
  }

  const rowSelection = state.rowSelection;
  const selectedIds = useMemo(() => selectionStateToSelectedIds(rowSelection), [rowSelection]);
  const selected = useMemo(() => selectedIds.map((id) => parseInt(id)), [selectedIds]);
  const selectedKeywords = selectedIds
    .map((id) => {
      const targetRow = seenRows.current.get(id);
      return (
        targetRow && {
          tags: targetRow.tags,
          target_source: targetRow.source,
          scheduled_actions: targetRow.scheduled_actions,
          id: parseInt(id),
          target_id: parseInt(id),
        }
      );
    })
    .filter((keyword) => keyword !== undefined);

  const resetSelection = () => setters.onRowSelectionChange({});
  const { bulkActionOptions } = useTargetBulkActions(selected, resetSelection, targetType, {
    keywords: selectedKeywords,
  });

  const table = useServerTable({
    data: tableData,
    columns,
    alternateSortingEnabled: true,
    getRowCanExpand: () => false,
    getRowId: (targetRow, index) => targetIdOf(targetType, targetRow)?.toString() ?? `row-${index}`,
    enableRowSelection: (tableRow) => {
      const idValue = targetIdOf(targetType, tableRow.original);
      return typeof idValue === "number" && !unmanagedTargetIds.includes(idValue);
    },
    rowCount: targetQuery.data?.count ?? 0,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: (targetQuery.isFetching && (!targetQuery.data || targetQuery.isPlaceholderData)) || isAdDataLoading,
      adsMapping,
      columnFilterSelectOptions: {
        state: TARGET_STATE_OPTIONS,
        match_type: KEYWORD_MATCH_TYPE_OPTIONS,
        source: channel === "walmart" ? WALMART_TARGET_SOURCE_OPTIONS : AMAZON_TARGET_SOURCE_OPTIONS,
        bid_trend_latest: BID_TREND_OPTIONS,
        bid_trend_slope: BID_TREND_OPTIONS,
      },
    } as { showSkeleton: boolean; adsMapping: AdsMapping },
  });

  return (
    <div className={css({ paddingLeft: "large", paddingRight: "large" })}>
      <Wrap
        className={css({
          justifyContent: "flex-end",
          width: "100%",
          maxWidth: "100%",
          padding: "small",
          fontSize: "large",
        })}
      >
        <BulkActionButton options={bulkActionOptions} selected={selected} isDisabled={!userCanUpdateAds} />
        {userCanUpdateAds && (
          <AddTargetsWidget
            initialData={prefilledTargets}
            buttonText={t(($) => $.sqpr["Add Search Query to Media Plans"])}
            targetType={targetType}
            renderAdSelector={true}
            onReset={resetSelection}
            descriptionText={t(
              ($) =>
                $.sqpr[
                  'Add query text to media plans by selecting one below and customizing match types. Once submitted, you can add the query text to more media plans by selecting another and clicking the "Add Keywords" button.'
                ],
            )}
          />
        )}
      </Wrap>
      <HStack width="100%" padding="small" fontSize="large">
        <VStack flex="1" alignItems="start">
          <HStack width="100%">
            <Button variant="contentOnly" onPress={() => resetTable(table)} fontSize="xxlarge">
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.sqpr["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <TableErrorWrapper error={targetQuery.error} userMessage={t(($) => $.sqpr["Error loading stats"])}>
        <Table table={table} className={css({ width: "max-content" })} />
      </TableErrorWrapper>
    </div>
  );
};
